import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/mikrolan-utan-uc-helg.jpg";

const MIKRO_UTAN_UC = [
  "credifi", "lumify", "ferratum", "saldo", "creditstar", "loanstep",
  "flexkonto", "merax", "tomly", "brixokk", "northmill", "klaralan", "fairlo",
];

const NAST_BAST = ["moneezy", "lendo", "sambla", "enklare", "axo"];

export const MIKROLAN_UTAN_UC_HELG: Record<string, Post> = {
  "mikrolan-utan-uc-1000-5000-kr-helg": {
    title: "Mikrolån utan UC 1 000–5 000 kr på helgen – jämförelse 2026",
    intro:
      "Det är lördag, kontot är tomt och banken öppnar inte förrän på måndag. Vi förstår – akuta utgifter bryr sig inte om öppettider. Ett mikrolån utan UC på 1 000–5 000 kr kan betalas ut även på helgen, ofta inom minuter. Här jämför vi alla långivare som prövar dig utan UC 2026, med ränta, avgifter, utbetalningstid och krav – och vi visar öppet vad ett litet lån faktiskt kostar innan du signerar.",
    author: "Erik Lindqvist",
    authorTitle: "Ekonomiredaktör",
    published: "2026-08-30",
    updated: "2026-08-30",
    heroSrc: hero,
    heroAlt: "Man i soffan ansöker om mikrolån utan UC på helgen med laptop och BankID",
    topLenderId: "credifi",
    bottomLenderId: "lumify",
    topLenderTitle: "Bäst i test – mikrolån utan UC på helgen",
    bottomLenderTitle: "Vår rekommendation för utbetalning på helgen",
    reviewsLenderId: "lumify",
    cta: { to: "/lan-utan-uc", label: "Se hela topplistan för lån utan UC" },
    calculator: {
      title: "Räkna på mikrolån utan UC",
      intro:
        "Ta trettio sekunder innan du ansöker. Dra i reglagen så ser du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta för ditt mikrolån.",
      note:
        "På små belopp väger uppläggningsavgift och aviavgift tyngre än själva räntan, och det är därför den effektiva räntan ser hög ut. Kort löptid ger högre månadskostnad men lägre total lånekostnad. Jämför alltid effektiv ränta mellan långivare – det är det enda måttet där alla avgifter räknas in.",
      amount: 5000,
      minAmount: 1000,
      maxAmount: 30000,
      months: 6,
      maxMonths: 36,
      rate: 23.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 295,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan jag verkligen få ett mikrolån utbetalt på en lördag?",
        a: "Ja. Långivare som använder realtidsbetalning, Swish, Trustly eller Brite betalar ut dygnet runt, även på röda dagar. Använder långivaren i stället vanlig bankfil sker utbetalningen först nästa bankdag.",
      },
      {
        q: "Vad betyder mikrolån utan UC?",
        a: "Att kreditupplysningen hämtas hos Creditsafe eller Dun & Bradstreet i stället för hos UC. Kreditprövningen görs alltid, den är obligatorisk enligt konsumentkreditlagen, men förfrågan syns inte i din UC-historik.",
      },
      {
        q: "Hur mycket kan jag låna utan UC på helgen?",
        a: "Vanligen 1 000–5 000 kronor som ny kund, och upp till 30 000–50 000 kronor när du har en betalningshistorik hos långivaren. Större belopp kräver oftast manuell handläggning och landar därför på måndagen.",
      },
      {
        q: "Vad kostar ett mikrolån på 3 000 kr?",
        a: "Med 23,9 procent nominell ränta, 295 kronor i uppläggningsavgift och 29 kronor per avi blir månadskostnaden cirka 553 kronor på 6 månader. Totalt betalar du då ungefär 3 320 kronor.",
      },
      {
        q: "Går det att låna med betalningsanmärkning?",
        a: "Ibland. Flera långivare gör en individuell bedömning om anmärkningen är äldre och reglerad och du har en stabil inkomst. Har du en pågående skuld hos Kronofogden nekas ansökan i regel.",
      },
      {
        q: "Behöver jag fast anställning?",
        a: "Nej, men du behöver en regelbunden inkomst som går att styrka. Det kan vara lön, pension, a-kassa, sjukersättning eller föräldrapenning beroende på långivare.",
      },
      {
        q: "Kan jag ångra mig efteråt?",
        a: "Ja, du har 14 dagars ångerrätt enligt konsumentkreditlagen. Du betalar tillbaka lånebeloppet plus ränta för de dagar du haft pengarna, men slipper resten av kostnaden.",
      },
      {
        q: "Vad händer om jag inte kan betala på förfallodagen?",
        a: "Kontakta långivaren i förväg – de flesta erbjuder anstånd eller en ny betalningsplan. Uteblir betalningen tillkommer påminnelseavgift och dröjsmålsränta, och efter inkasso kan ärendet gå vidare till Kronofogden.",
      },
    ],
    blocks: [
      { t: "h2", text: "Mikrolån utan UC på helgen – kort svar" },
      { t: "p", text: "Ja, det går att låna 1 000–5 000 kronor utan UC även på en lördag eller söndag. Förutsättningen är att långivaren använder realtidsbetalning, att du signerar med BankID och att kontot står i ditt eget namn. Då landar pengarna oftast inom fem till trettio minuter." },
      { t: "p", text: "Vi vet att den här sidan sällan öppnas på en lugn eftermiddag. Därför är vi raka: vi visar både hur snabbt det går och exakt vad det kostar, så att beslutet håller även på måndagen." },
      {
        t: "stats",
        items: [
          { label: "Beloppsspann", value: "1 000–5 000 kr" },
          { label: "Utbetalning på helg", value: "5–30 minuter" },
          { label: "Kostnad 3 000 kr / 6 mån", value: "ca 320 kr" },
        ],
      },

      { t: "h2", text: "Vad är ett mikrolån?" },
      { t: "p", text: "Mikrolån är små blancolån utan säkerhet, oftast mellan 1 000 och 10 000 kronor. De marknadsförs ibland som smslån eller [snabblån](/snabblan), men juridiskt är de samma sak och omfattas av samma konsumentkreditlag." },
      { t: "h3", text: "Typiska belopp och löptider" },
      { t: "p", text: "Som ny kund erbjuds du normalt 1 000–5 000 kronor med tre till tolv månaders löptid. Har du skött tidigare lån hos samma långivare höjs ofta både belopp och löptid vid nästa ansökan." },

      { t: "h2", text: "Varför utbetalning fungerar även på helger" },
      { t: "p", text: "Det svenska betalningssystemet hanterar numera betalningar i realtid dygnet runt. En vanlig banköverföring mellan olika banker skickas i klumpar några gånger per bankdag, medan en realtidsbetalning når mottagaren på sekunder även på en söndagskväll." },
      { t: "h3", text: "Så avgör du om långivaren betalar ut på helgen" },
      {
        t: "ul",
        items: [
          "Villkoren nämner Swish, Trustly, Brite eller Zimpler.",
          "Långivaren skriver ut 'utbetalning dygnet runt' eller 'även helger'.",
          "Ansökan går att signera med BankID utan manuellt steg.",
          "Beloppet ligger inom långivarens automatiserade spann.",
        ],
      },

      { t: "h2", text: "Bäst i test – mikrolån utan UC 2026" },
      { t: "p", text: "Vår ranking väger effektiv ränta tyngst, följt av utbetalningstid, tydlighet i villkoren, tillstånd hos Finansinspektionen och verkliga kundomdömen. Nedan hittar du alla långivare som prövar dig utan UC." },
      { t: "lenders", ids: MIKRO_UTAN_UC, title: "Alla långivare med mikrolån utan UC", note: "Samtliga använder Creditsafe eller Dun & Bradstreet. Räntan sätts individuellt efter kreditprövning och räkneexemplen är typexempel enligt konsumentkreditlagen." },

      { t: "h2", text: "Näst bästa alternativen – lägre ränta, något längre väntan" },
      { t: "p", text: "Är behovet inte akut inom timmen är en låneförmedlare nästan alltid billigare. Du får bud från flera banker till priset av en enda kreditupplysning, och pengarna kommer normalt inom ett till två bankdygn." },
      { t: "lenders", ids: NAST_BAST, title: "Förmedlare med lägre ränta men längre handläggning" },

      { t: "h2", text: "Vad kostar ett mikrolån på 1 000–5 000 kr?" },
      { t: "p", text: "Kostnaden består av nominell ränta, uppläggningsavgift och en aviavgift per månad. På små belopp är avgifterna ofta dyrare än räntan, vilket förklarar varför den effektiva räntan ser hög ut." },
      {
        t: "table",
        caption: "Räkneexempel: annuitetslån, 23,9 % nominell ränta, 295 kr uppläggning, 29 kr avi",
        head: ["Belopp", "Löptid", "Månadskostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["1 000 kr", "3 mån", "463 kr", "1 388 kr", "199 %"],
          ["3 000 kr", "6 mån", "553 kr", "3 320 kr", "58 %"],
          ["5 000 kr", "6 mån", "922 kr", "5 533 kr", "38,4 %"],
          ["5 000 kr", "12 mån", "487 kr", "5 848 kr", "34,7 %"],
        ],
      },
      { t: "p", text: "Lägg märke till hur den effektiva räntan skenar på de allra minsta beloppen. Det beror inte på ocker utan på att en fast uppläggningsavgift slår hårt när lånet bara är tusen kronor och tre månader långt." },

      { t: "h2", text: "Effektiv ränta – måttet du faktiskt ska jämföra" },
      { t: "p", text: "Effektiv ränta räknar in ränta, uppläggningsavgift och aviavgift och omvandlar allt till en årlig procentsats. Två lån med samma nominella ränta kan skilja tiotals procentenheter i effektiv ränta så snart avgifterna skiljer sig." },
      { t: "h3", text: "Så sänker du den effektiva räntan" },
      {
        t: "ul",
        items: [
          "Välj e-faktura eller autogiro – då försvinner aviavgiften hos många långivare.",
          "Korta löptiden med några månader.",
          "Låna exakt det du behöver, inte den maxsumma du erbjuds.",
          "Lös lånet i förtid när lönen kommer.",
        ],
      },

      { t: "h2", text: "Räntetak och kostnadstak – ditt skydd i lagen" },
      { t: "p", text: "Den nominella räntan får aldrig överstiga Riksbankens referensränta plus 20 procentenheter. De samlade kostnaderna för ränta, avgifter och dröjsmålsränta får aldrig överstiga lånebeloppet. Lånar du 5 000 kronor kan kostnaden alltså aldrig bli mer än 5 000 kronor." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt enligt konsumentkreditlagen.",
          "Rätt att lösa lånet i förtid utan extra avgift.",
          "Kreditprövning är obligatorisk – ingen seriös långivare hoppar över den.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Kreditupplysning utan UC – vem används i stället?" },
      {
        t: "table",
        caption: "Vanliga upplysningskällor bland mikrolångivare",
        head: ["Upplysningsbolag", "Används av", "Syns i din UC?"],
        rows: [
          ["Creditsafe", "De flesta mikrolångivare", "Nej"],
          ["Dun & Bradstreet", "Flera kontokreditgivare", "Nej"],
          ["UC", "Banker och låneförmedlare", "Ja"],
        ],
      },
      { t: "p", text: "Skillnaden märks framför allt om du planerar att söka bolån eller ett större [privatlån](/privatlan) inom kort. Många UC-förfrågningar kan då tolkas som ansträngd ekonomi." },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "ul",
        items: [
          "Minst 18 år – flera långivare kräver 20 eller 21 år.",
          "Folkbokförd i Sverige med svenskt personnummer.",
          "Mobilt BankID för signering.",
          "Regelbunden inkomst som går att styrka.",
          "Bankkonto i eget namn för utbetalningen.",
        ],
      },

      { t: "h2", text: "Om du har betalningsanmärkning eller skuld hos Kronofogden" },
      { t: "p", text: "En äldre, reglerad anmärkning stänger inte alla dörrar. En pågående skuld hos [Kronofogden](https://www.kronofogden.se) gör det däremot i praktiken. Läs mer på vår sida om [lån med betalningsanmärkning](/lan-med-betalningsanmarkning-bast-i-test-30000-kr)." },

      { t: "h2", text: "Så ansöker du på helgen – steg för steg" },
      {
        t: "steps",
        title: "Från ansökan till pengar på kontot",
        items: [
          "Bestäm exakt belopp och löptid – räkna först, ansök sen.",
          "Välj en långivare som betalar ut i realtid.",
          "Fyll i inkomst, boende och försörjningsbörda.",
          "Signera ansökan med BankID.",
          "Få besked direkt och utbetalning inom minuter.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med mikrolån utan UC" },
      {
        t: "proscons",
        pros: [
          "Utbetalning även på kvällar och helger",
          "Ingen UC-förfrågan i din kreditakt",
          "Ingen säkerhet eller borgensman krävs",
          "Små belopp med kort löptid",
        ],
        cons: [
          "Hög effektiv ränta i förhållande till beloppet",
          "Fasta avgifter slår hårt på små lån",
          "Kort löptid ger hög månadskostnad",
          "Lätt att bygga upp flera små krediter",
        ],
      },

      { t: "h2", text: "Vanliga misstag när det är bråttom" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor att undvika",
        items: [
          "Att ansöka hos flera långivare samtidigt.",
          "Att välja längsta löptiden för att månadskostnaden ska se låg ut.",
          "Att jämföra nominell ränta i stället för effektiv ränta.",
          "Att låna för att betala av ett annat mikrolån.",
        ],
      },

      { t: "h2", text: "Mikrolån eller kontokredit på helgen?" },
      {
        t: "table",
        caption: "Två sätt att lösa ett litet akut behov",
        head: ["", "Mikrolån", "Kontokredit"],
        rows: [
          ["Belopp", "1 000–30 000 kr", "1 000–50 000 kr"],
          ["Kostnad när krediten inte används", "Lånet är utbetalat", "Ofta 0 kr"],
          ["Utbetalning", "Minuter", "Minuter efter beviljande"],
          ["Passar för", "Ett känt engångsbelopp", "Återkommande buffertbehov"],
        ],
      },

      { t: "h2", text: "Billigare alternativ innan du lånar" },
      {
        t: "ul",
        items: [
          "Avbetalningsplan direkt hos fordringsägaren, ofta räntefri.",
          "En befintlig kontokredit eller ett kort med lägre ränta.",
          "Samla dyra småkrediter i ett [samlingslån](/samla-lan).",
          "Kommunens budget- och skuldrådgivning, som alltid är kostnadsfri.",
        ],
      },

      { t: "h2", text: "Så undviker du att fastna i en skuldspiral" },
      { t: "p", text: "Ett mikrolån är rimligast när behovet är litet, tillfälligt och du redan vet vilken månad det är löst. Behöver du låna igen inom samma kvartal är det ett tecken på att problemet är strukturellt snarare än akut – och då är budgetrådgivning ett bättre första steg än en ny kredit." },

      { t: "h2", text: "Helgens tidslinje – vad som händer när" },
      { t: "p", text: "Många tror att en helgansökan hanteras av en person någonstans. I själva verket är hela kedjan automatiserad: identifiering med BankID, kreditupplysning hos Creditsafe, poängsättning, avtalssignering och utbetalning sker maskinellt på under en minut. Det är därför tidpunkten spelar mindre roll än långivarens tekniska val." },
      { t: "h3", text: "Lördag förmiddag" },
      { t: "p", text: "Den bästa tidpunkten. Alla realtidssystem är igång, och eventuell manuell komplettering hinner hanteras av kundtjänst som ofta har helgbemanning fram till eftermiddagen." },
      { t: "h3", text: "Söndag kväll" },
      { t: "p", text: "Fungerar hos långivare med realtidsbetalning, men om något behöver kompletteras riskerar ärendet att ligga till måndag morgon. Ansök hellre tidigare på dagen om du kan." },

      { t: "h2", text: "Vanliga anledningar till att pengarna dröjer" },
      { t: "p", text: "Även hos en långivare som betalar ut dygnet runt kan enskilda utbetalningar fastna. Nästan alltid handlar det om något av fyra skäl, och tre av dem kan du påverka själv innan du ansöker." },
      {
        t: "ul",
        items: [
          "Kontonumret är felskrivet eller tillhör någon annan.",
          "Beloppet ligger över långivarens automatiserade gräns.",
          "Inkomstuppgiften behöver styrkas med underlag.",
          "Din bank stödjer inte omedelbara insättningar.",
        ],
      },

      { t: "h2", text: "Att jämföra långivare på tio minuter" },
      { t: "p", text: "Du behöver inte läsa varje villkorsdokument. Fyra siffror räcker långt: effektiv ränta på ditt belopp, uppläggningsavgift, aviavgift och kortaste tillåtna löptid. Skriv upp dem för tre långivare och skillnaden blir tydlig direkt." },
      { t: "h3", text: "Checklista innan du signerar" },
      {
        t: "ul",
        items: [
          "Effektiv ränta uträknad på exakt ditt belopp och din löptid.",
          "Totalbeloppet i kronor, inte bara månadskostnaden.",
          "Vad en försenad betalning kostar i påminnelseavgift.",
          "Om aviavgiften försvinner med e-faktura eller autogiro.",
          "Att långivaren finns i Finansinspektionens register.",
        ],
      },

      { t: "h2", text: "Vad händer efter utbetalningen?" },
      { t: "p", text: "Första avin kommer normalt inom en månad. Lägg upp e-faktura direkt, gärna samma dag som pengarna kommer, så slipper du både aviavgift och risken att missa förfallodagen. Sätt också en påminnelse i telefonen tre dagar innan varje betalning." },

      { t: "h2", text: "Om ekonomin blir tuffare än väntat" },
      { t: "p", text: "Livet går sällan enligt plan. Om du märker att en betalning inte kommer att gå igenom, hör av dig till långivaren innan förfallodagen – inte efter. Nästan alla erbjuder anstånd eller en ändrad betalningsplan, och det kostar betydligt mindre än ett inkassoärende." },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, transparens, tillstånd hos Finansinspektionen och kundomdömen. Ingen långivare kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Ett mikrolån utan UC på 1 000–5 000 kronor kan lösa ett akut behov mitt på en lördag, men kostnaden i förhållande till beloppet är hög. Välj kortast möjliga löptid, jämför effektiv ränta, välj e-faktura för att slippa aviavgiften – och lös lånet så snart lönen kommer." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Riksbanken](https://www.riksbank.se) – referensränta och realtidsbetalningar",
          "[Kronofogden](https://www.kronofogden.se) – vad som händer vid utebliven betalning",
        ],
      },
    ],
  },
};
